"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { PartyPopper, Search, Star } from "lucide-react";
import { useEffect, useMemo, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  loadTokens,
  type OrderRecord,
  type PositionRecord,
  type MyTradesPage,
  type MyTradeRow,
  type BookmarkRow,
} from "@/lib/api";
import { WinShareModal } from "@/components/social/WinShareModal";
import { TradePanelModal } from "@/components/TradePanelModal";
import { useDisplayCurrency } from "@/lib/useDisplayCurrency";
import { useFxRate } from "@/lib/useFxRate";
import {
  convertCents,
  formatMoney,
  formatPriceCents,
  formatShares,
  positionValueCents,
  type MoneyCurrency,
} from "@/lib/format";
// STORY-813 PR 5c — compat helper retired; the matcher now writes
// only the new vocabulary so `status === "live"` inline works.
import { Skeleton } from "@/components/Skeleton";

/**
 * Polymarket-style dashboard surface — replaces the side-by-side
 * Orders + Positions cards (MyOrdersAndPositions) with a single
 * tabbed view + sub-filter + search + sort. Dashboard refactor
 * 2026-04-29.
 *
 * Tabs:
 *   Positions — what the user holds; sub-filter Active / Closed
 *               (closed = resolved markets where the user had a
 *               position at resolution time).
 *   Orders    — open or historical limit/market orders; sub-filter
 *               Active (open + partially_filled) / Closed (filled +
 *               cancelled).
 *   Activity  — fill history (every trade the user was buyer or
 *               seller on). Read-only.
 *
 * Search + sort apply per-tab. Sort options: Alphabetically (by
 * market title), Most recent (by created/updated), Value (positions
 * by current value, orders by reservation, activity by notional).
 */

type Tab = "positions" | "orders" | "activity" | "bookmarks";

// 2026-05-11 — narrow `?tab=…` query param so /home?tab=orders deep-
// links land on the Orders tab. Used by OrderPlacedToast + TradePanel
// "Cancel pending" copy so users have a real destination for the
// "Open Orders" reference (previously linked to /portfolio/orders
// which 404'd).
function parseTabParam(value: string | null): Tab | null {
  if (
    value === "positions" ||
    value === "orders" ||
    value === "activity" ||
    value === "bookmarks"
  ) {
    return value;
  }
  return null;
}
type SubFilter = "active" | "closed";
type SortKey = "alphabetical" | "recent" | "value";

const TAB_IDS: Tab[] = ["positions", "orders", "activity", "bookmarks"];

export function DashboardTabs() {
  const { t } = useTranslation();
  const tabLabels: Record<Tab, string> = {
    positions: t("dashboardTabs.positions"),
    orders: t("dashboardTabs.orders"),
    activity: t("dashboardTabs.activity"),
    bookmarks: t("dashboardTabs.bookmarks"),
  };
  // useSearchParams can return null when the component renders outside
  // a Next.js navigation context (e.g. jsdom tests without next/navigation
  // mocks). Optional-chain to keep tests + SSR safe.
  const searchParams = useSearchParams();
  const initialTab =
    parseTabParam(searchParams?.get("tab") ?? null) ?? "positions";
  const [tab, setTab] = useState<Tab>(initialTab);
  const [subFilter, setSubFilter] = useState<SubFilter>("active");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");

  const [positions, setPositions] = useState<PositionRecord[]>([]);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [trades, setTrades] = useState<MyTradeRow[]>([]);
  const [bookmarks, setBookmarks] = useState<BookmarkRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load data for the active tab on tab change OR sub-filter change.
  // 30s poll + window focus + balance-change events match the
  // legacy MyOrdersAndPositions polling contract so the page feels
  // live without a per-user WS room.
  useEffect(() => {
    if (!loadTokens()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(false);
      return;
    }
    let cancelled = false;
    let pollHandle: ReturnType<typeof setInterval> | null = null;

    async function load(reason: "initial" | "refresh") {
      try {
        if (tab === "positions") {
          // 2026-05-11 — also fetch open orders so each position row can
          // surface an inline "○ N sh resting at X¢" status when the
          // user has pending sells on that outcome. Polymarket / Kalshi
          // pattern: dashboard surfaces pending state on the same row
          // it shows the position; no redirect, no modal-stuck UX.
          // Founder direction: "why do we need to create a new page if
          // we can use here on the dashboard."
          const [allPositions, openOrders] = await Promise.all([
            api.listMyPositions(),
            api.listMyOrders("open").catch(() => [] as OrderRecord[]),
          ]);
          if (cancelled) return;
          setPositions(allPositions);
          setOrders(openOrders);
        } else if (tab === "orders") {
          const filter = subFilter === "active" ? "open" : "closed";
          const list = await api.listMyOrders(filter);
          if (cancelled) return;
          setOrders(list);
        } else if (tab === "activity") {
          const page = await api.listMyTrades({ limit: 100 });
          if (cancelled) return;
          setTrades((page as MyTradesPage).trades);
        } else {
          // bookmarks tab
          const list = await api.listBookmarks();
          if (cancelled) return;
          setBookmarks(list);
        }
        if (!cancelled) {
          setLoading(false);
          setError(null);
        }
      } catch (err) {
        if (cancelled || reason === "refresh") return;
        setLoading(false);
        setError(
          err instanceof Error ? err.message : t("dashboardTabs.loadFailed"),
        );
      }
    }

    void load("initial");
    pollHandle = setInterval(() => void load("refresh"), 30_000);
    const onFocus = () => void load("refresh");
    const onBalanceChanged = () => void load("refresh");
    window.addEventListener("focus", onFocus);
    window.addEventListener("granbay.balance.changed", onBalanceChanged);
    return () => {
      cancelled = true;
      if (pollHandle) clearInterval(pollHandle);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("granbay.balance.changed", onBalanceChanged);
    };
    // `t` is stable per react-i18next contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, subFilter]);

  // Filtered + sorted views per tab. useMemo avoids re-sorting on
  // unrelated state changes (tab switches, search typing).
  const filteredPositions = useMemo(() => {
    let list = [...positions];
    // Active vs Closed: market resolved → closed.
    list = list.filter((p) =>
      subFilter === "active"
        ? p.marketStatus !== "resolved"
        : p.marketStatus === "resolved",
    );
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((p) =>
        (p.marketTitle.en ?? "").toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      if (sort === "alphabetical") {
        return (a.marketTitle.en ?? "").localeCompare(b.marketTitle.en ?? "");
      }
      if (sort === "value") {
        return (
          positionValueCents(b.shares, b.currentPriceCents) -
          positionValueCents(a.shares, a.currentPriceCents)
        );
      }
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    return list;
  }, [positions, subFilter, search, sort]);

  const filteredOrders = useMemo(() => {
    let list = [...orders];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((o) => {
        const title =
          typeof o.marketTitle === "object" ? (o.marketTitle?.en ?? "") : "";
        return title.toLowerCase().includes(q);
      });
    }
    list.sort((a, b) => {
      if (sort === "alphabetical") {
        const at =
          typeof a.marketTitle === "object" ? (a.marketTitle?.en ?? "") : "";
        const bt =
          typeof b.marketTitle === "object" ? (b.marketTitle?.en ?? "") : "";
        return at.localeCompare(bt);
      }
      if (sort === "value") {
        const av =
          (a.price_cents ?? 0) *
          (Number(a.size_shares) - Number(a.filled_shares));
        const bv =
          (b.price_cents ?? 0) *
          (Number(b.size_shares) - Number(b.filled_shares));
        return bv - av;
      }
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
    return list;
  }, [orders, search, sort]);

  // Snapshot "now" once on mount so the activity-window filter is
  // stable across renders (react-hooks/purity rule blocks Date.now()
  // inside useMemo). Refreshes on data reload via the polling
  // effect because that's when the data slice changes anyway.
  const [activityNow] = useState(() => Date.now());
  const filteredTrades = useMemo(() => {
    const titleStr = (raw: MyTradeRow["marketTitle"]): string =>
      typeof raw === "object" ? (raw.en ?? "") : (raw ?? "");
    let list = [...trades];
    // Activity sub-filter: Active = last 7 days, Closed = older
    list = list.filter((t) => {
      const ageDays =
        (activityNow - new Date(t.occurredAt).getTime()) / 86_400_000;
      return subFilter === "active" ? ageDays <= 7 : ageDays > 7;
    });
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((t) =>
        titleStr(t.marketTitle).toLowerCase().includes(q),
      );
    }
    list.sort((a, b) => {
      if (sort === "alphabetical") {
        return titleStr(a.marketTitle).localeCompare(titleStr(b.marketTitle));
      }
      if (sort === "value") {
        const av = Number(a.sizeShares) * a.priceCents;
        const bv = Number(b.sizeShares) * b.priceCents;
        return bv - av;
      }
      return (
        new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()
      );
    });
    return list;
  }, [trades, subFilter, search, sort, activityNow]);

  const filteredBookmarks = useMemo(() => {
    let list = [...bookmarks];
    // Active vs Closed: market resolved → closed.
    list = list.filter((b) =>
      subFilter === "active"
        ? b.status !== "resolved"
        : b.status === "resolved",
    );
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((b) => (b.title.en ?? "").toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (sort === "alphabetical") {
        return (a.title.en ?? "").localeCompare(b.title.en ?? "");
      }
      if (sort === "value") {
        return (b.yesPriceCents ?? 0) - (a.yesPriceCents ?? 0);
      }
      // 'recent' → newest bookmark first
      return (
        new Date(b.bookmarkedAt).getTime() - new Date(a.bookmarkedAt).getTime()
      );
    });
    return list;
  }, [bookmarks, subFilter, search, sort]);

  return (
    <section className="mt-8">
      {/* Tab strip */}
      <div className="flex items-baseline gap-5 border-b border-[var(--color-border)]">
        {TAB_IDS.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`relative pb-2 text-[15px] font-semibold transition-colors ${
              tab === id
                ? "text-[var(--color-text)]"
                : "text-[var(--color-text3)] hover:text-[var(--color-text2)]"
            }`}
          >
            {tabLabels[id]}
            {tab === id ? (
              <span
                className="absolute -bottom-px left-0 right-0 h-[2px] rounded-full"
                style={{ background: "var(--color-text)" }}
              />
            ) : null}
          </button>
        ))}
      </div>

      {/* Sub-filter + search + sort row */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-0.5">
          {(["active", "closed"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setSubFilter(f)}
              className={`min-h-[36px] rounded-sm px-3 py-1 text-[12px] font-semibold transition-colors md:min-h-0 ${
                subFilter === f
                  ? "bg-[var(--color-bg)] text-[var(--color-text)]"
                  : "text-[var(--color-text2)] hover:text-[var(--color-text)]"
              }`}
            >
              {f === "active"
                ? t("dashboardTabs.active")
                : t("dashboardTabs.closed")}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search
            size={12}
            strokeWidth={1.75}
            aria-hidden="true"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text3)] opacity-70"
          />
          <input
            type="search"
            placeholder={t("dashboardTabs.searchPlaceholder", {
              tab: tabLabels[tab].toLowerCase(),
            })}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] py-1.5 pl-7 pr-3 text-[12px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
          />
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="h-8 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-2 text-[12px] text-[var(--color-text)] outline-none focus:border-[var(--color-brand)]"
        >
          <option value="recent">{t("dashboardTabs.sortRecent")}</option>
          <option value="alphabetical">
            {t("dashboardTabs.sortAlphabetical")}
          </option>
          <option value="value">{t("dashboardTabs.sortValue")}</option>
        </select>
      </div>

      {/* Body */}
      <div className="mt-4">
        {loading ? (
          <div
            role="status"
            aria-label={t("dashboardTabs.loadingAria")}
            className="space-y-2"
          >
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-full rounded-md" />
            <Skeleton className="h-10 w-full rounded-md" />
          </div>
        ) : error ? (
          <p
            role="alert"
            className="py-3 text-[12px]"
            style={{ color: "var(--color-no)" }}
          >
            {error}
          </p>
        ) : tab === "positions" ? (
          <PositionsTable rows={filteredPositions} openOrders={orders} />
        ) : tab === "orders" ? (
          <OrdersTable
            rows={filteredOrders}
            onCancelled={(id) =>
              setOrders((prev) => prev.filter((o) => o.id !== id))
            }
          />
        ) : tab === "activity" ? (
          <ActivityTable rows={filteredTrades} />
        ) : (
          <BookmarksTable rows={filteredBookmarks} />
        )}
      </div>
    </section>
  );
}

function PositionsTable({
  rows,
  openOrders,
}: {
  rows: PositionRecord[];
  // 2026-05-11 — open orders passed in so each position row can
  // surface inline "○ N sh resting at X¢" status when the user has
  // pending sells on that outcome. Dashboard becomes the single
  // surface for both held + pending state per position; close-modal
  // no longer leaves the user wondering "where's my order?".
  openOrders: OrderRecord[];
}) {
  const { t } = useTranslation();
  const displayCurrency = useDisplayCurrency();
  // 2026-05-12 — position rows previously used
  // `formatMoney(usdcCents, displayCurrency)` which slapped the right
  // symbol on raw USDC numbers but DID NOT convert. Result: summary
  // tile said R$3.081 (converted) while the row below showed R$628
  // (raw USDC) on the same render — visible asymmetry founder
  // reported tonight. Now BR users get USDC × rate; non-BR pass-
  // through. Falls back to raw USDC labeled "USDC" if rate hasn't
  // loaded — honest fallback.
  const fxBrl = useFxRate("BRL");
  const usdcPerBrl = fxBrl?.rate ?? null;
  const canConvertBrl = usdcPerBrl !== null && usdcPerBrl > 0;
  const rowCurrency: MoneyCurrency =
    displayCurrency === "BRL" && canConvertBrl
      ? "BRL"
      : displayCurrency === "BRL"
        ? "USDC"
        : displayCurrency;
  const formatUsdcForRow = (usdcCents: number): string => {
    if (rowCurrency === "BRL") {
      const brl =
        convertCents(usdcCents, "USDC", "BRL", usdcPerBrl) ?? usdcCents;
      return formatMoney(brl, "BRL");
    }
    return formatMoney(usdcCents, rowCurrency);
  };
  // Group open SELL orders by (marketId, outcomeId) for inline rendering
  // under each position row. listMyOrders("open") may return either
  // camelCase or snake_case fields depending on the API serializer's
  // current state (see OrderRecord type — snake_case is canonical for
  // backwards compat).
  const pendingSellsByOutcome = useMemo(() => {
    const map = new Map<
      string,
      Array<{ id: string; remainingShares: number; priceCents: number }>
    >();
    for (const o of openOrders) {
      if (o.side !== "sell") continue;
      // listMine's SQL aliases `o.market_id AS "marketId"` and
      // `o.outcome_id AS "outcomeId"` — the response carries the
      // camelCase keys. The legacy snake_case keys stay on the
      // OrderRecord type only as a backwards-compat slot for callers
      // hitting older serializer paths (place-order response, etc).
      // Read both so this works regardless of which serializer the
      // upstream `listMyOrders("open")` call ends up routing through.
      const marketId = o.marketId ?? o.market_id;
      const outcomeId = o.outcomeId ?? o.outcome_id;
      if (!marketId || !outcomeId) continue;
      const size = Number(o.size_shares);
      const filled = Number(o.filled_shares);
      const remaining = size - filled;
      if (remaining <= 0) continue;
      const key = `${marketId}:${outcomeId}`;
      const list = map.get(key) ?? [];
      list.push({
        id: o.id,
        remainingShares: remaining,
        priceCents: o.price_cents ?? 0,
      });
      map.set(key, list);
    }
    return map;
  }, [openOrders]);
  const [shareWin, setShareWin] = useState<{
    amountCents: number;
    marketTitle: string;
    marketSlug: string;
    outcome: "YES" | "NO";
    winType: "small" | "big" | "milestone";
  } | null>(null);
  const [username, setUsername] = useState<string>("");
  // 2026-04-30 — restore eToro-style close-position from /home dashboard
  // (originally on MyOrdersAndPositions; lost when DashboardTabs took
  // over the surface in PR #353). Same ClosePositionModal — boots in
  // shares mode at 100% holdings, market-SELLs via api.placeOrder,
  // refreshes positions via the existing granbay.balance.changed event.
  const [closeTargetId, setCloseTargetId] = useState<string | null>(null);
  const closeTarget = closeTargetId
    ? (rows.find((p) => p.id === closeTargetId) ?? null)
    : null;
  // 2026-05-11 — when user clicks [Edit] on a pending sell row, carry
  // the existing order's amount + price so the modal opens pre-filled
  // for tweak-and-replace (not a fresh close). Cleared when closeTargetId
  // clears so subsequent fresh-Close clicks don't pick up stale prefill.
  //
  // 2026-05-12 — `orderId` added so the modal can hand it to TradePanel
  // as `editingOrderId`. That puts the panel into cancel-then-replace
  // mode (excludes the order from reservedShares, cancels it on
  // submit before placing the new one). Without this, the modal
  // claimed "30.42 held" while pre-filling 887.70 shares — the user
  // saw two contradictory share counts on a money screen.
  const [editPrefill, setEditPrefill] = useState<{
    initialAmount: string;
    initialPriceCents: number;
    orderId: string;
  } | null>(null);
  // [Cancel] flow surfaces an inline error if the API rejects.
  const [cancellingPendingId, setCancellingPendingId] = useState<string | null>(
    null,
  );
  const [cancelPendingError, setCancelPendingError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    void api
      .me()
      .then((u) => setUsername(u.username))
      .catch(() => {
        /* fallback: leave blank — modal handles missing username */
      });
  }, []);

  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-[12px] text-[var(--color-text3)]">
        {t("dashboardTabs.noPositions")}
      </p>
    );
  }
  return (
    // 2026-05-02 — was `overflow-hidden`, which clipped the rightmost
    // Actions column (Close button) past the viewport on a 375px
    // iPhone with no scroll affordance. Founder bug surfaced from
    // screenshot. Now `overflow-x-auto` so the column scrolls into
    // view; AVG + CURRENT are also hidden below md so the four-
    // column-on-mobile layout (Market + Value + Close) fits without
    // scrolling for the common case.
    <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text3)]">
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colMarket")}
            </th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
              {t("dashboardTabs.colAvg")}
            </th>
            <th
              className="hidden px-3 py-2 text-right font-semibold md:table-cell"
              title="Last trade price for this outcome — the live best bid (what you'd actually sell into) may differ, especially on thin books. The Close-position modal shows both."
            >
              {t("dashboardTabs.colCurrent")}
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {t("dashboardTabs.colValue")}
            </th>
            <th className="px-3 py-2 text-right font-semibold sr-only">
              {t("dashboardTabs.colActions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            // All position-math goes through lib/format helpers per the
            // 2026-05-11 discipline rule. Component-inline `shares ×
            // price` arithmetic is the bug class that produced the 100×
            // overstatement founder report — code that doesn't compute
            // can't drift.
            const valueCents = positionValueCents(
              p.shares,
              p.currentPriceCents,
            );
            const costCents = positionValueCents(p.shares, p.avgCostCents);
            const pnlCents = valueCents - costCents;
            const pnlPct = costCents > 0 ? (pnlCents / costCents) * 100 : 0;
            // STORY-610 — show "Share win" button on resolved
            // positions where the user is on the winning side.
            const isResolvedWin =
              p.resolvedAt !== null &&
              p.resolvedOutcomeId !== null &&
              p.resolvedOutcomeId === p.outcomeId;
            const winAmountCents = isResolvedWin ? Math.max(0, pnlCents) : 0;
            // 2026-05-11 — inline pending-sell status. Founder direction:
            // "why do we need to create a new page if we can use here
            // on the dashboard." After a close-modal submit, the user
            // sees the resting state right here on the same row that
            // shows their position — no toast, no redirect, no
            // dead-end UX.
            const pendingSells =
              pendingSellsByOutcome.get(`${p.marketId}:${p.outcomeId}`) ?? [];
            const pendingShares = pendingSells.reduce(
              (acc, o) => acc + o.remainingShares,
              0,
            );
            const hasPending = pendingShares > 0;
            // 2026-05-11 — availableShares = held centi minus reserved-
            // in-pending-sells centi. When availableShares === 0 AND
            // hasPending, the standalone Close button disappears (only
            // inline Edit/Cancel make sense).
            const availableShares = Math.max(
              0,
              Number(p.shares) - pendingShares,
            );
            return (
              <tr key={p.id} className="border-t border-[var(--color-border)]">
                <td className="px-3 py-2">
                  <Link
                    href={`/markets/${p.marketSlug}`}
                    className="font-semibold hover:underline"
                  >
                    {p.marketTitle.en ?? p.marketSlug}
                  </Link>
                  <div className="mt-0.5 text-[10px] text-[var(--color-text3)]">
                    {p.outcomeName.en ??
                      t("dashboardTabs.outcomePrefix", {
                        n: p.outcomeSortOrder,
                      })}{" "}
                    · {formatShares(p.shares)} {t("dashboardTabs.shares")}
                  </div>
                  {hasPending && (
                    <div
                      className="mt-1 flex flex-wrap items-center gap-2 text-[10px]"
                      data-testid="position-pending-status"
                    >
                      <span
                        className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono tabular-nums"
                        style={{
                          background:
                            "color-mix(in srgb, var(--color-status-caution) 10%, transparent)",
                          color: "var(--color-status-caution)",
                        }}
                      >
                        <span aria-hidden="true">○</span>
                        <span>
                          {t("dashboardTabs.pendingResting", {
                            shares: formatShares(pendingShares),
                            cents:
                              pendingSells.length === 1
                                ? pendingSells[0].priceCents
                                : pendingSells
                                    .map((o) => o.priceCents)
                                    .filter((c, i, a) => a.indexOf(c) === i)
                                    .join("/"),
                            defaultValue: `{{shares}} sh resting at {{cents}}¢`,
                          })}
                        </span>
                      </span>
                      {/* 2026-05-11 — inline Edit/Cancel affordance per
                          founder direction: manage pending orders on
                          the position row itself, not via the Orders
                          tab. Edit opens the modal pre-filled with the
                          first pending order's amount + price (the
                          common case — one resting limit per outcome).
                          When multiple pending orders exist, Edit
                          tweaks the most recent + the cancel-and-
                          replace path inside TradePanel cancels all
                          others on submit. */}
                      <button
                        type="button"
                        onClick={() => {
                          const first = pendingSells[0];
                          if (!first) return;
                          setEditPrefill({
                            initialAmount: (
                              first.remainingShares / 100
                            ).toFixed(2),
                            initialPriceCents: first.priceCents,
                            orderId: first.id,
                          });
                          setCloseTargetId(p.id);
                          setCancelPendingError(null);
                        }}
                        disabled={cancellingPendingId !== null}
                        className="rounded-md border px-2 py-0.5 text-[10px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                        style={{
                          borderColor: "var(--color-status-caution)",
                          color: "var(--color-status-caution)",
                        }}
                        data-testid={`pending-edit-${p.id}`}
                      >
                        {t("dashboardTabs.edit", { defaultValue: "Edit" })}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          const first = pendingSells[0];
                          if (!first || cancellingPendingId) return;
                          setCancellingPendingId(first.id);
                          setCancelPendingError(null);
                          try {
                            await api.cancelOrder(first.id);
                            window.dispatchEvent(
                              new Event("granbay.balance.changed"),
                            );
                          } catch (err) {
                            setCancelPendingError(
                              err instanceof Error
                                ? err.message
                                : "Couldn't cancel order.",
                            );
                          } finally {
                            setCancellingPendingId(null);
                          }
                        }}
                        disabled={cancellingPendingId !== null}
                        className="text-[10px] underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                        style={{ color: "var(--color-text2)" }}
                        data-testid={`pending-cancel-${p.id}`}
                      >
                        {cancellingPendingId === pendingSells[0]?.id
                          ? t("dashboardTabs.cancellingOrder", {
                              defaultValue: "Cancelling…",
                            })
                          : t("dashboardTabs.cancelOrder", {
                              defaultValue: "Cancel",
                            })}
                      </button>
                      {cancelPendingError && (
                        <span
                          role="alert"
                          className="text-[10px]"
                          style={{ color: "var(--color-no)" }}
                        >
                          {cancelPendingError}
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="hidden px-3 py-2 text-right font-mono tabular-nums md:table-cell">
                  {formatPriceCents(Number(p.avgCostCents))}
                </td>
                <td className="hidden px-3 py-2 text-right font-mono tabular-nums md:table-cell">
                  {formatPriceCents(p.currentPriceCents)}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="font-mono font-semibold tabular-nums">
                    {formatUsdcForRow(valueCents)}
                  </div>
                  <div
                    className="text-[10px] font-mono tabular-nums"
                    style={{
                      color:
                        pnlCents >= 0
                          ? "var(--color-status-positive, #15803d)"
                          : "var(--color-status-negative, #dc2626)",
                    }}
                  >
                    {pnlCents >= 0 ? "+" : ""}
                    {formatUsdcForRow(pnlCents)} (
                    {pnlPct >= 0 ? "+" : ""}
                    {pnlPct.toFixed(1)}%)
                  </div>
                  {isResolvedWin && winAmountCents > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setShareWin({
                          amountCents: winAmountCents,
                          marketTitle: p.marketTitle.en ?? p.marketSlug,
                          marketSlug: p.marketSlug,
                          outcome:
                            (p.outcomeSortOrder ?? 0) === 0 ? "YES" : "NO",
                          winType:
                            winAmountCents >= 100_000
                              ? "milestone"
                              : winAmountCents >= 50_000
                                ? "big"
                                : "small",
                        })
                      }
                      className="mt-1 inline-flex items-center gap-1 rounded-md border border-[var(--color-brand)] bg-[var(--color-brand-tint)] px-2 py-0.5 text-[10px] font-bold text-[var(--color-brand)] hover:bg-[var(--color-brand)] hover:text-white"
                    >
                      <PartyPopper
                        size={10}
                        strokeWidth={2}
                        aria-hidden="true"
                      />
                      {t("dashboardTabs.shareWin")}
                    </button>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {/* eToro-style close — only on still-open
                      positions (no resolution + non-zero shares).
                      Resolved winners get the Share-win button
                      already; resolved losers have nothing to
                      close.
                      2026-05-11 — when the user already has pending
                      SELL orders on this outcome, button label
                      transforms to "Modify". Same modal opens; the
                      modal's submit path internally cancels-and-
                      replaces the pending orders. Founder direction:
                      "gray out the close button or change the
                      button to modify/edit the close request." */}
                  {/* 2026-05-11 — when every share is locked in pending
                      sells (availableShares === 0 + hasPending), the
                      Close button disappears entirely. Edit/Cancel on
                      the inline row above are the only meaningful
                      actions — nothing fresh to close. Founder
                      direction: "[Close] disappears entirely — only
                      Edit + Cancel make sense, since there's nothing
                      left to close fresh." */}
                  {p.resolvedAt === null &&
                    Number(p.shares) > 0 &&
                    !(hasPending && availableShares === 0) && (
                      <button
                        type="button"
                        onClick={() => {
                          setEditPrefill(null);
                          setCloseTargetId(p.id);
                        }}
                        className="min-h-[36px] rounded-md border px-2 py-1 text-[10px] font-bold transition-colors md:min-h-0"
                        style={{
                          borderColor: "var(--color-no)",
                          color: "var(--color-no)",
                        }}
                        title={t("dashboardTabs.closeTitle")}
                      >
                        {t("dashboardTabs.close")}
                      </button>
                    )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {shareWin && username && (
        <WinShareModal
          win={shareWin}
          username={username}
          onClose={() => setShareWin(null)}
        />
      )}
      {closeTarget && (
        <TradePanelModal
          // 2026-05-12 — key by (position id, editing order id) so a
          // second Edit click on a different row remounts the modal
          // and TradePanel re-initializes its useState() seeds with
          // the new prefill. Without the key, switching rows while
          // the modal is open carries the previous row's side +
          // amount + limit price into the new context.
          key={`${closeTarget.id}:${editPrefill?.orderId ?? "close"}`}
          marketSlug={closeTarget.marketSlug}
          outcomeSortOrder={closeTarget.outcomeSortOrder}
          initialAmount={editPrefill?.initialAmount}
          initialPriceCents={editPrefill?.initialPriceCents}
          editingOrderId={editPrefill?.orderId}
          onClose={() => {
            setCloseTargetId(null);
            setEditPrefill(null);
          }}
        />
      )}
    </div>
  );
}

function OrdersTable({
  rows,
  onCancelled,
}: {
  rows: OrderRecord[];
  // 2026-04-30 — restored cancel-order from the legacy
  // MyOrdersAndPositions surface (lost in PR #353). Parent removes
  // the row optimistically; the polling refresh + balance-changed
  // listener confirm against the backend on the next tick.
  onCancelled: (orderId: string) => void;
}) {
  const { t } = useTranslation();
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  async function handleCancel(orderId: string) {
    setCancelling(orderId);
    setCancelError(null);
    startTransition(async () => {
      try {
        await api.cancelOrder(orderId);
        onCancelled(orderId);
        window.dispatchEvent(new CustomEvent("granbay.balance.changed"));
      } catch (err) {
        const msg =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("dashboardTabs.cancelFailed");
        // Mirror the legacy "already terminal" silent-swallow so
        // double-clicks don't surface a scary error.
        const alreadyTerminal =
          /Cannot cancel order in status (cancelled|filled)/i.test(msg);
        if (alreadyTerminal) {
          onCancelled(orderId);
        } else {
          setCancelError(msg);
          setTimeout(() => setCancelError((s) => (s === msg ? null : s)), 5000);
        }
      } finally {
        setCancelling(null);
      }
    });
  }

  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-[12px] text-[var(--color-text3)]">
        {t("dashboardTabs.noOrders")}
      </p>
    );
  }
  return (
    // Same overflow-hidden → overflow-x-auto + mobile column hiding
    // pattern as the Positions table above. PRICE + FILLED are
    // hidden below md so MARKET + SIDE + STATUS + Cancel fit cleanly
    // in the 375px viewport.
    <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
      {cancelError && (
        <p
          role="alert"
          className="border-b border-[var(--color-border)] px-3 py-2 text-[11px]"
          style={{ color: "var(--color-no)" }}
        >
          {cancelError}
        </p>
      )}
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text3)]">
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colMarket")}
            </th>
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colSide")}
            </th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
              {t("dashboardTabs.colPrice")}
            </th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
              {t("dashboardTabs.colFilled")}
            </th>
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colStatus")}
            </th>
            <th className="px-3 py-2 text-right font-semibold sr-only">
              {t("dashboardTabs.colActions")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const title =
              typeof o.marketTitle === "object"
                ? (o.marketTitle?.en ?? o.marketSlug ?? "")
                : (o.marketSlug ?? "");
            const slug = o.marketSlug;
            // Cancel only for still-cancellable orders (status='live'
            // is the only non-terminal value post-0156).
            const isCancellable = o.status === "live";
            return (
              <tr key={o.id} className="border-t border-[var(--color-border)]">
                <td className="px-3 py-2">
                  {slug ? (
                    <Link
                      href={`/markets/${slug}`}
                      className="font-semibold hover:underline"
                    >
                      {title}
                    </Link>
                  ) : (
                    <span className="font-semibold">{title}</span>
                  )}
                  <div className="mt-0.5 text-[10px] text-[var(--color-text3)]">
                    {typeof o.outcomeName === "object"
                      ? (o.outcomeName?.en ?? "")
                      : ""}
                  </div>
                </td>
                <td className="px-3 py-2 font-semibold uppercase">{o.side}</td>
                <td className="hidden px-3 py-2 text-right font-mono tabular-nums md:table-cell">
                  {o.price_cents !== null
                    ? `${o.price_cents}¢`
                    : t("dashboardTabs.marketCt")}
                </td>
                <td className="hidden px-3 py-2 text-right font-mono tabular-nums md:table-cell">
                  {o.filled_shares}/{o.size_shares}
                </td>
                <td className="px-3 py-2 text-[var(--color-text2)]">
                  {o.status}
                </td>
                <td className="px-3 py-2 text-right">
                  {isCancellable && (
                    <button
                      type="button"
                      disabled={cancelling === o.id}
                      onClick={() => handleCancel(o.id)}
                      className="min-h-[36px] rounded-md border px-2 py-1 text-[10px] font-bold transition-colors disabled:opacity-50 md:min-h-0"
                      style={{
                        borderColor: "var(--color-no)",
                        color: "var(--color-no)",
                      }}
                    >
                      {cancelling === o.id
                        ? t("dashboardTabs.cancelling")
                        : t("dashboardTabs.cancel")}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ActivityTable({ rows }: { rows: MyTradeRow[] }) {
  const { t } = useTranslation();
  const displayCurrency = useDisplayCurrency();
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-[12px] text-[var(--color-text3)]">
        {t("dashboardTabs.noActivity")}
      </p>
    );
  }
  const titleStr = (raw: MyTradeRow["marketTitle"]): string =>
    typeof raw === "object" ? (raw.en ?? "") : (raw ?? "");
  const outcomeStr = (raw: MyTradeRow["outcomeName"]): string =>
    typeof raw === "object" ? (raw.en ?? "") : (raw ?? "");
  return (
    <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text3)]">
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colWhen")}
            </th>
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colMarket")}
            </th>
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colSide")}
            </th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
              {t("dashboardTabs.colPrice")}
            </th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
              {t("dashboardTabs.colShares")}
            </th>
            <th className="hidden px-3 py-2 text-right font-semibold md:table-cell">
              {t("dashboardTabs.colFee")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr
              key={t.tradeId}
              className="border-t border-[var(--color-border)]"
            >
              <td className="px-3 py-2 text-[var(--color-text3)]">
                {fmtRelative(t.occurredAt)}
              </td>
              <td className="px-3 py-2">
                <Link
                  href={`/markets/${t.marketSlug}`}
                  className="font-semibold hover:underline"
                >
                  {titleStr(t.marketTitle) || t.marketSlug}
                </Link>
                <div className="mt-0.5 text-[10px] text-[var(--color-text3)]">
                  {outcomeStr(t.outcomeName)}
                </div>
                {/* Mobile-only inline meta — restores price + shares
                    + fee that the md:table-cell columns hide. Desktop
                    keeps them in their own columns; mobile collapses
                    them into a single muted line under the title. */}
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-[var(--color-text3)] md:hidden">
                  <span className="font-mono tabular-nums">
                    {t.priceCents}¢
                  </span>
                  <span className="font-mono tabular-nums">
                    {t.sizeShares} sh
                  </span>
                  <span className="font-mono tabular-nums">
                    {formatMoney(Number(t.feeCents), displayCurrency)}
                  </span>
                </div>
              </td>
              <td className="px-3 py-2 font-semibold uppercase">
                {t.direction}
              </td>
              <td className="hidden px-3 py-2 text-right font-mono tabular-nums md:table-cell">
                {t.priceCents}¢
              </td>
              <td className="hidden px-3 py-2 text-right font-mono tabular-nums md:table-cell">
                {t.sizeShares}
              </td>
              <td className="hidden px-3 py-2 text-right font-mono tabular-nums text-[var(--color-text3)] md:table-cell">
                {formatMoney(Number(t.feeCents), displayCurrency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BookmarksTable({ rows }: { rows: BookmarkRow[] }) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return (
      <p className="flex items-center justify-center gap-1.5 py-6 text-center text-[12px] text-[var(--color-text3)]">
        <Star size={12} strokeWidth={1.75} aria-hidden="true" />
        {t("dashboardTabs.noBookmarks")}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-[var(--color-text3)]">
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colMarket")}
            </th>
            <th className="px-3 py-2 text-right font-semibold">
              {t("dashboardTabs.colYesPrice")}
            </th>
            <th className="px-3 py-2 font-semibold">
              {t("dashboardTabs.colStatus")}
            </th>
            <th className="hidden px-3 py-2 font-semibold md:table-cell">
              {t("dashboardTabs.colCloses")}
            </th>
            <th className="hidden px-3 py-2 font-semibold md:table-cell">
              {t("dashboardTabs.colSaved")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr
              key={b.marketId}
              className="border-t border-[var(--color-border)]"
            >
              <td className="px-3 py-2">
                <Link
                  href={`/markets/${b.slug}`}
                  className="font-semibold hover:underline"
                >
                  {b.title.en ?? b.slug}
                </Link>
                {/* Mobile-only meta restores the closes + saved
                    timestamps the md:table-cell columns hide. */}
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-[var(--color-text3)] md:hidden">
                  <span>{fmtRelativeForward(b.closesAt)}</span>
                  <span>· saved {fmtRelative(b.bookmarkedAt)}</span>
                </div>
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums">
                {b.yesPriceCents !== null ? `${b.yesPriceCents}¢` : "—"}
              </td>
              <td className="px-3 py-2 text-[var(--color-text2)] capitalize">
                {b.status}
              </td>
              <td className="hidden px-3 py-2 text-[var(--color-text3)] md:table-cell">
                {fmtRelativeForward(b.closesAt)}
              </td>
              <td className="hidden px-3 py-2 text-[var(--color-text3)] md:table-cell">
                {fmtRelative(b.bookmarkedAt)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function fmtRelativeForward(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = t - Date.now();
  if (diff <= 0) return "closed";
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  const days = Math.floor(hrs / 24);
  return `in ${days}d`;
}

function fmtRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
